import { apiClient } from './apiClient';

/**
 * Interface cho bài viết đã xóa
 */
export interface DeletedPost {
  id: number;
  title: string;
  content: string;
  author: string;
  category: string;
  deletedAt: string;
  deletedBy: string;
  status: string;
  slug?: string;
  featuredImage?: string;
}

/**
 * Interface cho danh mục đã xóa
 */
export interface DeletedCategory {
  id: number;
  name: string;
  description: string;
  postCount: number;
  deletedAt: string;
  deletedBy: string;
  status: string;
  slug?: string;
}

/**
 * Interface cho bot đã xóa
 */
export interface DeletedBot {
  id: number;
  name: string;
  description: string;
  keywords: string[];
  isActive: boolean;
  deletedAt: string;
  deletedBy: string;
  status: string;
}

/**
 * Interface cho điều khoản đã xóa
 */
export interface DeletedTerm {
  id: number;
  title: string;
  content: string;
  version: string;
  isActive: boolean;
  deletedAt: string;
  deletedBy: string;
  status: string;
}

/**
 * Interface cho chính sách bảo mật đã xóa
 */
export interface DeletedPrivacyPolicy {
  id: number;
  title: string;
  content: string;
  version: string;
  isActive: boolean;
  filePath?: string;
  originalFileName?: string;
  deletedAt: string;
  deletedBy?: number | null;
  status: string;
}

/**
 * Interface cho người dùng đã xóa
 */
export interface DeletedUser {
  id: number;
  username: string;
  email: string;
  fullName: string;
  role: string;
  deletedAt: string;
  deletedBy: string;
  status: string;
}

/**
 * Interface cho response API
 */
export interface TrashResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Service cho quản lý thùng rác
 */
class TrashService {
  /**
   * Lấy danh sách bài viết đã xóa
   */
  async getDeletedPosts(page: number = 1, limit: number = 10): Promise<TrashResponse<DeletedPost>> {
    const response = await apiClient.get(`/admin/trash/posts?page=${page}&limit=${limit}`);
    if (!response.ok) {
      throw new Error('Không thể tải danh sách bài viết đã xóa');
    }
    return response.json();
  }

  /**
   * Khôi phục bài viết
   */
  async restorePost(postId: number): Promise<void> {
    const response = await apiClient.post(`/admin/trash/posts/${postId}/restore`);
    if (!response.ok) {
      throw new Error('Không thể khôi phục bài viết');
    }
  }

  /**
   * Xóa vĩnh viễn bài viết
   */
  async permanentDeletePost(postId: number): Promise<void> {
    const response = await apiClient.delete(`/admin/trash/posts/${postId}`);
    if (!response.ok) {
      throw new Error('Không thể xóa vĩnh viễn bài viết');
    }
  }

  /**
   * Khôi phục nhiều bài viết
   */
  async bulkRestorePosts(postIds: number[]): Promise<void> {
    const response = await apiClient.post('/admin/trash/posts/bulk-restore', { ids: postIds });
    if (!response.ok) {
      throw new Error('Không thể khôi phục các bài viết');
    }
  }

  /**
   * Xóa vĩnh viễn nhiều bài viết
   */
  async bulkDeletePosts(postIds: number[]): Promise<void> {
    const response = await apiClient.post('/admin/trash/posts/bulk-delete', { ids: postIds });
    if (!response.ok) {
      throw new Error('Không thể xóa vĩnh viễn các bài viết');
    }
  }

  /**
   * Lấy danh sách danh mục đã xóa
   */
  async getDeletedCategories(page: number = 1, limit: number = 10): Promise<TrashResponse<DeletedCategory>> {
    const response = await apiClient.get(`/admin/trash/categories?page=${page}&limit=${limit}`);
    if (!response.ok) {
      throw new Error('Không thể tải danh sách danh mục đã xóa');
    }
    return response.json();
  }

  /**
   * Khôi phục danh mục
   */
  async restoreCategory(categoryId: number): Promise<void> {
    const response = await apiClient.post(`/admin/trash/categories/${categoryId}/restore`);
    if (!response.ok) {
      throw new Error('Không thể khôi phục danh mục');
    }
  }

  /**
   * Xóa vĩnh viễn danh mục
   */
  async permanentDeleteCategory(categoryId: number): Promise<void> {
    const response = await apiClient.delete(`/admin/trash/categories/${categoryId}`);
    if (!response.ok) {
      throw new Error('Không thể xóa vĩnh viễn danh mục');
    }
  }

  /**
   * Khôi phục nhiều danh mục
   */
  async bulkRestoreCategories(categoryIds: number[]): Promise<void> {
    const response = await apiClient.post('/admin/trash/categories/bulk-restore', { ids: categoryIds });
    if (!response.ok) {
      throw new Error('Không thể khôi phục các danh mục');
    }
  }

  /**
   * Xóa vĩnh viễn nhiều danh mục
   */
  async bulkDeleteCategories(categoryIds: number[]): Promise<void> {
    const response = await apiClient.post('/admin/trash/categories/bulk-delete', { ids: categoryIds });
    if (!response.ok) {
      throw new Error('Không thể xóa vĩnh viễn các danh mục');
    }
  }

  /**
   * Lấy danh sách bot đã xóa
   */
  async getDeletedBots(page: number = 1, limit: number = 10): Promise<TrashResponse<DeletedBot>> {
    const response = await apiClient.get(`/admin/trash/bots?page=${page}&limit=${limit}`);
    if (!response.ok) {
      throw new Error('Không thể tải danh sách bot đã xóa');
    }
    return response.json();
  }

  /**
   * Khôi phục bot
   */
  async restoreBot(botId: number): Promise<void> {
    const response = await apiClient.post(`/admin/trash/bots/${botId}/restore`);
    if (!response.ok) {
      throw new Error('Không thể khôi phục bot');
    }
  }

  /**
   * Xóa vĩnh viễn bot
   */
  async permanentDeleteBot(botId: number): Promise<void> {
    const response = await apiClient.delete(`/admin/trash/bots/${botId}`);
    if (!response.ok) {
      throw new Error('Không thể xóa vĩnh viễn bot');
    }
  }

  /**
   * Khôi phục nhiều bot
   */
  async bulkRestoreBots(botIds: number[]): Promise<void> {
    const response = await apiClient.post('/admin/trash/bots/bulk-restore', { ids: botIds });
    if (!response.ok) {
      throw new Error('Không thể khôi phục các bot');
    }
  }

  /**
   * Xóa vĩnh viễn nhiều bot
   */
  async bulkDeleteBots(botIds: number[]): Promise<void> {
    const response = await apiClient.post('/admin/trash/bots/bulk-delete', { ids: botIds });
    if (!response.ok) {
      throw new Error('Không thể xóa vĩnh viễn các bot');
    }
  }

  /**
   * Lấy danh sách điều khoản đã xóa
   */
  async getDeletedTerms(page: number = 1, limit: number = 10): Promise<TrashResponse<DeletedTerm>> {
    const response = await apiClient.get(`/admin/trash/terms?page=${page}&limit=${limit}`);
    if (!response.ok) {
      throw new Error('Không thể tải danh sách điều khoản đã xóa');
    }
    return response.json();
  }

  /**
   * Khôi phục điều khoản
   */
  async restoreTerm(termId: number): Promise<void> {
    const response = await apiClient.post(`/admin/trash/terms/${termId}/restore`);
    if (!response.ok) {
      throw new Error('Không thể khôi phục điều khoản');
    }
  }

  /**
   * Xóa vĩnh viễn điều khoản
   */
  async permanentDeleteTerm(termId: number): Promise<void> {
    const response = await apiClient.delete(`/admin/trash/terms/${termId}`);
    if (!response.ok) {
      throw new Error('Không thể xóa vĩnh viễn điều khoản');
    }
  }

  /**
   * Khôi phục nhiều điều khoản
   */
  async bulkRestoreTerms(termIds: number[]): Promise<void> {
    const response = await apiClient.post('/admin/trash/terms/bulk-restore', { ids: termIds });
    if (!response.ok) {
      throw new Error('Không thể khôi phục các điều khoản');
    }
  }

  /**
   * Xóa vĩnh viễn nhiều điều khoản
   */
  async bulkDeleteTerms(termIds: number[]): Promise<void> {
    const response = await apiClient.post('/admin/trash/terms/bulk-delete', { ids: termIds });
    if (!response.ok) {
      throw new Error('Không thể xóa vĩnh viễn các điều khoản');
    }
  }

  /**
   * Lấy danh sách người dùng đã xóa
   */
  async getDeletedUsers(page: number = 1, limit: number = 10): Promise<TrashResponse<DeletedUser>> {
    const response = await apiClient.get(`/admin/trash/users?page=${page}&limit=${limit}`);
    if (!response.ok) {
      throw new Error('Không thể tải danh sách người dùng đã xóa');
    }
    return response.json();
  }

  /**
   * Khôi phục người dùng
   */
  async restoreUser(userId: number): Promise<void> {
    const response = await apiClient.post(`/admin/trash/users/${userId}/restore`);
    if (!response.ok) {
      throw new Error('Không thể khôi phục người dùng');
    }
  }

  /**
   * Xóa vĩnh viễn người dùng
   */
  async permanentDeleteUser(userId: number): Promise<void> {
    const response = await apiClient.delete(`/admin/trash/users/${userId}`);
    if (!response.ok) {
      throw new Error('Không thể xóa vĩnh viễn người dùng');
    }
  }

  /**
   * Khôi phục nhiều người dùng
   */
  async bulkRestoreUsers(userIds: number[]): Promise<void> {
    const response = await apiClient.post('/admin/trash/users/bulk-restore', { ids: userIds });
    if (!response.ok) {
      throw new Error('Không thể khôi phục các người dùng');
    }
  }

  /**
   * Xóa vĩnh viễn nhiều người dùng
   */
  async bulkDeleteUsers(userIds: number[]): Promise<void> {
    const response = await apiClient.post('/admin/trash/users/bulk-delete', { ids: userIds });
    if (!response.ok) {
      throw new Error('Không thể xóa vĩnh viễn các người dùng');
    }
  }

  // ===== PRIVACY POLICIES METHODS =====

  /**
   * Lấy danh sách chính sách bảo mật đã xóa
   */
  async getDeletedPrivacyPolicies(page: number = 1, limit: number = 10): Promise<TrashResponse<DeletedPrivacyPolicy>> {
    const response = await apiClient.get(`/privacy-policies/deleted?page=${page}&limit=${limit}`);
    if (!response.ok) {
      throw new Error('Không thể tải danh sách chính sách bảo mật đã xóa');
    }
    return response.json();
  }

  /**
   * Khôi phục chính sách bảo mật
   */
  async restorePrivacyPolicy(policyId: number): Promise<void> {
    const response = await apiClient.post(`/privacy-policies/${policyId}/restore`);
    if (!response.ok) {
      throw new Error('Không thể khôi phục chính sách bảo mật');
    }
  }

  /**
   * Xóa vĩnh viễn chính sách bảo mật
   */
  async permanentDeletePrivacyPolicy(policyId: number): Promise<void> {
    const response = await apiClient.delete(`/privacy-policies/${policyId}/permanent`);
    if (!response.ok) {
      throw new Error('Không thể xóa vĩnh viễn chính sách bảo mật');
    }
  }

  /**
   * Khôi phục nhiều chính sách bảo mật
   */
  async bulkRestorePrivacyPolicies(policyIds: number[]): Promise<void> {
    const response = await apiClient.post('/privacy-policies/bulk-restore', { ids: policyIds });
    if (!response.ok) {
      throw new Error('Không thể khôi phục các chính sách bảo mật');
    }
  }

  /**
   * Xóa vĩnh viễn nhiều chính sách bảo mật
   */
  async bulkDeletePrivacyPolicies(policyIds: number[]): Promise<void> {
    const response = await apiClient.post('/privacy-policies/bulk-delete', { ids: policyIds });
    if (!response.ok) {
      throw new Error('Không thể xóa vĩnh viễn các chính sách bảo mật');
    }
  }
}

// Export singleton instance
export const trashService = new TrashService();
